import React, { useCallback, useMemo, useState } from 'react'
import { Alert, ScrollView, StyleSheet, View } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { hasOwnerRights } from '@ppwr/convex/lounges/shared'
import { useConfirmDialog } from '../../components/ConfirmDialog'
import { RetentionDropdown } from './RetentionDropdown'
import { translate } from '../../i18n'
import { SettingsScreenLayout, SettingsLoadingView } from '@/components/layouts/SettingsScreenLayout'
import { ToggleRow } from '@/components/layouts/ToggleRow'
import { SettingsCard } from '@/components/layouts/SettingsCard'

export function LoungeConfigurationScreen() {
  const { loungeId: loungeIdStr } = useLocalSearchParams<{ loungeId: string }>()
  const loungeId = loungeIdStr as Id<'lounges'> | undefined
  const router = useRouter()
  const {
    session: { user }
  } = useStore()
  const userId = user?.auth0Id ?? ''

  const lobby = useQuery(api.lounges.queries.getLoungeLobby, loungeId ? { loungeId, userId } : 'skip')
  const updateSettings = useMutation(api.lounges.mutations.updateLoungeSettings)
  const muteNotifications = useMutation(api.lounges.mutations.muteNotifications)
  const leaveLounge = useMutation(api.lounges.mutations.leaveLounge)

  const [leaving, setLeaving] = useState(false)
  const { showConfirm, dialogPortal } = useConfirmDialog()

  const callerMember = lobby?.members.find((m) => m.userId === userId)
  const role = callerMember?.role
  const isOwner = useMemo(() => role === 'owner', [role])
  const isOwnerOrCoowner = useMemo(() => role && hasOwnerRights(role), [role])

  const settings = lobby?.settings

  const patchSettings = useCallback(
    async (patch: Partial<NonNullable<typeof settings>>) => {
      if (!loungeId) return
      try {
        await updateSettings({
          loungeId: loungeId as Id<'lounges'>,
          actorId: userId,
          settings: patch
        })
      } catch (err: any) {
        Alert.alert(
          translate('common.error'),
          err.message ?? translate('loungeConfigurationScreen.errors.updateSettingsFailed')
        )
      }
    },
    [loungeId, userId, updateSettings]
  )

  const handleLeave = useCallback(() => {
    if (!loungeId) return
    if (isOwner) {
      showConfirm({
        title: translate('loungeSettingsScreen.cannotLeaveTitle'),
        message: translate('loungeSettingsScreen.cannotLeaveMessage'),
        cancelText: 'OK',
        confirmText: 'OK',
        onConfirm: () => {}
      })
      return
    }
    showConfirm({
      title: translate('loungeSettingsScreen.leaveAlert.title'),
      message: translate('loungeSettingsScreen.leaveAlert.message'),
      cancelText: translate('loungeSettingsScreen.leaveAlert.cancel'),
      confirmText: translate('loungeSettingsScreen.leaveAlert.confirm'),
      onConfirm: async () => {
        setLeaving(true)
        try {
          await leaveLounge({ loungeId: loungeId as Id<'lounges'>, userId })
          router.replace('/lounges')
        } catch (err: any) {
          showConfirm({
            title: 'Error',
            message: err.message ?? translate('loungeSettingsScreen.leaveError'),
            cancelText: 'OK',
            confirmText: 'OK',
            onConfirm: () => {}
          })
        } finally {
          setLeaving(false)
        }
      }
    })
  }, [loungeId, userId, isOwner, leaveLounge, router, showConfirm])

  if (!lobby || !settings) {
    return (
      <SettingsScreenLayout onBack={() => router.back()}>
        <SettingsLoadingView />
      </SettingsScreenLayout>
    )
  }

  if (!isOwnerOrCoowner) {
    return (
      <SettingsScreenLayout title={translate('loungeConfigurationScreen.title')} onBack={() => router.back()}>
        {dialogPortal}
        <ScrollView contentContainerStyle={styles.content}>
          <SettingsCard gap={0}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeConfigurationScreen.alerts.sectionLabel')}
            </DS.Text>
            <ToggleRow
              label={translate('loungeConfigurationScreen.alerts.enablePushNotifications')}
              value={!(callerMember?.notificationsMuted ?? false)}
              onValueChange={(enabled) =>
                muteNotifications({ loungeId: loungeId as Id<'lounges'>, userId, muted: !enabled })
              }
            />
          </SettingsCard>
          <DS.Button
            type="On Light"
            variant="Large/Primary"
            backgroundColor="Brand/Maroon"
            onPress={handleLeave}
            disabled={leaving}
          >
            {leaving ? translate('loungeSettingsScreen.leaving') : translate('loungeSettingsScreen.leaveLounge')}
          </DS.Button>
        </ScrollView>
      </SettingsScreenLayout>
    )
  }

  return (
    <SettingsScreenLayout title={translate('loungeConfigurationScreen.title')} onBack={() => router.back()}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <SettingsCard gap={32}>
          {/* Chat Settings */}
          <View style={styles.group}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeConfigurationScreen.chatSettings.sectionLabel')}
            </DS.Text>
            <ToggleRow
              label={translate('loungeConfigurationScreen.chatSettings.enableTextChat')}
              value={settings.chatEnabled}
              onValueChange={(v) => patchSettings({ chatEnabled: v })}
            />
            <View style={styles.retentionRow}>
              <DS.Text variant="Body Large/Regular" style={{ flex: 1 }}>
                {translate('loungeConfigurationScreen.chatSettings.chatRetention')}
              </DS.Text>
              <RetentionDropdown
                value={settings.chatRetentionDays}
                onChange={(days) => {
                  if (days !== settings.chatRetentionDays) {
                    patchSettings({ chatRetentionDays: days })
                  }
                }}
              />
            </View>
          </View>

          {/* Game Permissions */}
          <View style={styles.group}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeConfigurationScreen.gamePermissions.sectionLabel')}
            </DS.Text>
            <ToggleRow
              label={translate('loungeConfigurationScreen.gamePermissions.allowAdHocGames')}
              value={settings.adHocGamesEnabled}
              onValueChange={(v) => patchSettings({ adHocGamesEnabled: v })}
            />
            <ToggleRow
              label={translate('loungeConfigurationScreen.gamePermissions.membersCanInvite')}
              value={settings.membersCanInvite ?? false}
              onValueChange={(v) => patchSettings({ membersCanInvite: v })}
            />
          </View>

          {/* Alerts & Notifications */}
          <View style={styles.group}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeConfigurationScreen.alerts.sectionLabel')}
            </DS.Text>
            <ToggleRow
              label={translate('loungeConfigurationScreen.alerts.enablePushNotifications')}
              value={settings.pushNotificationsEnabled ?? true}
              onValueChange={(v) => patchSettings({ pushNotificationsEnabled: v })}
            />
          </View>
        </SettingsCard>
      </ScrollView>
    </SettingsScreenLayout>
  )
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 24
  },
  section: {
    gap: 8
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: 0.5,
    color: DS.palette['Brand/Dark Grey'],
    marginBottom: 4
  },
  group: {
    gap: 12
  },
  retentionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    gap: 12
  }
})
